import {
  Priorities,
  type Project,
  type ProjectInput,
  projectInputSchema,
  ProjectStatuses,
  routes,
} from '@pmai/contracts';
import { CheckboxField, FormDialog, SelectField, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { useCustomerOptions } from '../../entities/customers/customer-options';
import { PROJECT_DERIVED_VIEWS } from '../../entities/projects/derived-views';
import { bind, bindSelect } from '../../shared/forms/form';
import { useEntityForm } from '../../shared/forms/use-entity-form';
import { useEnumLabel } from '../../shared/i18n';

/** New project: planning, low priority, financial management on (FR-PRJ-003/005). */
const empty: ProjectInput = {
  name: '',
  customerId: '',
  status: 'planning',
  priority: 'low',
  startDate: '',
  plannedFinish: '',
  actualFinish: '',
  completion: '',
  financialManagement: true,
  jiraProjectKeys: '',
  jiraEpicKeys: '',
  charterUrl: '',
};

const valuesOf = (p: Project): ProjectInput => ({
  name: p.name,
  customerId: p.customerId,
  status: p.status,
  priority: p.priority,
  startDate: p.startDate,
  plannedFinish: p.plannedFinish,
  actualFinish: p.actualFinish ?? '',
  completion: p.completion === null ? '' : String(p.completion),
  financialManagement: p.financialManagement,
  jiraProjectKeys: p.jiraProjectKeys.join(', '),
  jiraEpicKeys: p.jiraEpicKeys.join(', '),
  charterUrl: p.charterUrl ?? '',
});

/** "New project" / "Edit project" (FR-PRJ-001…009). */
export function ProjectDialog({
  open,
  project,
  onClose,
  onSaved,
}: {
  open: boolean;
  project: Project | null;
  onClose: () => void;
  onSaved?: (project: Project) => void;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const customers = useCustomerOptions(open);
  const { form, create, update, dialog } = useEntityForm({
    schema: projectInputSchema,
    open,
    record: project,
    values: project ? valuesOf(project) : empty,
    create: routes.projects.create,
    update: routes.projects.update,
    invalidates: [
      routes.projects.list,
      routes.projects.get,
      routes.projects.options,
      ...PROJECT_DERIVED_VIEWS,
    ],
    onClose,
    onSaved,
    success: { create: 'projects.created', update: 'projects.updated' },
  });

  const f = (field: string) => t(`projects.fields.${field}`);
  return (
    <FormDialog
      {...dialog}
      size="lg"
      title={project ? t('projects.editTitle') : t('projects.new')}
      onSubmit={form.handleSubmit((body) =>
        project
          ? update.mutate({ params: { id: project.id }, body: { ...body, version: project.version } })
          : create.mutate({ body }),
      )}
    >
      <TextField label={f('name')} required {...bind(form, 'name')} />
      <SelectField
        label={f('customer')}
        required
        placeholder={t('common.notSpecified')}
        options={customers}
        {...bindSelect(form, 'customerId')}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label={f('status')}
          required
          options={ProjectStatuses.options.map((o) => ({ value: o.code, label: label(o) }))}
          {...bind(form, 'status')}
        />
        <SelectField
          label={f('priority')}
          required
          options={Priorities.options.map((o) => ({ value: o.code, label: label(o) }))}
          {...bind(form, 'priority')}
        />
        <TextField label={f('startDate')} type="date" required {...bind(form, 'startDate')} />
        <TextField label={f('plannedFinish')} type="date" required {...bind(form, 'plannedFinish')} />
        <TextField label={f('actualFinish')} type="date" {...bind(form, 'actualFinish')} />
        <TextField label={f('completion')} inputMode="numeric" {...bind(form, 'completion')} />
        <TextField label={f('jiraProjectKeys')} hint={f('jiraKeysHint')} {...bind(form, 'jiraProjectKeys')} />
        <TextField label={f('jiraEpicKeys')} hint={f('jiraKeysHint')} {...bind(form, 'jiraEpicKeys')} />
      </div>
      <TextField label={f('charterUrl')} type="url" placeholder="https://" {...bind(form, 'charterUrl')} />
      <CheckboxField label={`💰 ${f('financialManagement')}`} {...form.register('financialManagement')} />
    </FormDialog>
  );
}
